import api from './api';

// ギャラリー機能のAPI層。
// 単位=画像1枚（倉庫思想）。アップロードは非同期処理（Celery）。
// status=0は処理中、status=1で表示可能。フロントはステータスをポーリング。

export interface GalleryImage {
  id: number;
  user_id: number;
  username: string;
  display_name: string;
  avatar: string | null;
  image_url: string | null;       // status=1のみ
  thumb_url: string | null;       // status=1のみ
  width: number | null;
  height: number | null;
  size_bytes: number;
  resolution_tier: number;        // 1:1280 / 2:1920 / 3:2560
  visibility: number;             // 0:公開 1:フォロワー 2:非公開
  is_spoiler: boolean;
  caption: string | null;
  game: {
    id: number;
    name: string;
    cover_image: string | null;
  } | null;
  reaction_counts: Record<string, number> | null;
  user_reaction: number | null;
  status: number;                 // 0:処理中 1:公開 9:削除
  created_at: string;
  updated_at: string;
}

export interface GalleryQuota {
  plan: number;
  billing_period: 'monthly' | 'yearly' | null;  // FREE時はnull
  count_used: number;
  count_limit: number;
  storage_used_bytes: number;
  storage_limit_bytes: number;
  resolution_options: number[];
  // 月契約者のみ。年契約・FREEはnull（制限なし）
  monthly_upload_used: number | null;
  monthly_upload_limit: number | null;
}

export interface GalleryUploadParams {
  file: File;
  caption?: string;
  game_id?: number;
  resolution_tier?: number;       // 1/2/3 default:1
  visibility?: number;            // 0/1/2 default:0
  is_spoiler?: boolean;
}

export interface GalleryStatus {
  id: number;
  status: number;                 // 0/1/9
  image_url: string | null;
  thumb_url: string | null;
  width: number | null;
  height: number | null;
  size_bytes: number;
}

// 自分のギャラリー一覧
export const getMyGallery = async (page = 1, gameId?: number) => {
  const params: Record<string, string | number> = { page };
  if (gameId) params.game_id = gameId;
  const response = await api.get('/user-gallery/', { params });
  return response.data as {
    count: number;
    next: string | null;
    previous: string | null;
    results: GalleryImage[];
  };
};

// 公開ギャラリー閲覧（ユーザー指定）
export const getPublicGallery = async (username: string, page = 1, gameId?: number) => {
  const params: Record<string, string | number> = { username, page };
  if (gameId) params.game_id = gameId;
  const response = await api.get('/public-gallery/by_user/', { params });
  return response.data as {
    count: number;
    next: string | null;
    previous: string | null;
    results: GalleryImage[];
  };
};

// 単一画像取得（通知からのディープリンク用。自分の画像のみ）
export const getGalleryImage = async (imageId: number): Promise<GalleryImage> => {
  const response = await api.get(`/user-gallery/${imageId}/`);
  return response.data;
};

// クォータ取得
export const getGalleryQuota = async (): Promise<GalleryQuota> => {
  const response = await api.get('/user-gallery/quota/');
  return response.data;
};

// アクティビティフィードのギャラリー投稿ペイロード（type=5）
export interface FeedGalleryImage {
  id: number;
  thumb_url: string | null;
  image_url: string | null;
  width: number | null;
  height: number | null;
  caption: string | null;
  is_spoiler: boolean;
}
export interface FeedGalleryPayload {
  group_key: string;
  count: number;
  images: FeedGalleryImage[];   // 先頭最大3枚
}

// フィードのギャラリーグループ全画像（リアクション・メタ込み）。ライトボックス用。
export const getGalleryGroup = async (groupKey: string): Promise<GalleryImage[]> => {
  const response = await api.get(`/public-gallery/group/${groupKey}/`);
  return response.data;
};

// 画像アップロード
export const uploadGalleryImage = async (
  params: GalleryUploadParams,
  onProgress?: (percent: number) => void,
): Promise<GalleryImage> => {
  const formData = new FormData();
  formData.append('file', params.file);
  if (params.caption) formData.append('caption', params.caption);
  if (params.game_id != null) formData.append('game_id', String(params.game_id));
  if (params.resolution_tier != null) formData.append('resolution_tier', String(params.resolution_tier));
  if (params.visibility != null) formData.append('visibility', String(params.visibility));
  if (params.is_spoiler != null) formData.append('is_spoiler', String(params.is_spoiler));

  const response = await api.post('/user-gallery/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
      ? (e) => {
          if (e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        }
      : undefined,
  });
  return response.data;
};

// 処理ステータス取得（ポーリング用）
export const getGalleryStatus = async (imageId: number): Promise<GalleryStatus> => {
  const response = await api.get(`/user-gallery/${imageId}/status/`);
  return response.data;
};

// 編集（caption/visibility/is_spoiler/game_id）
export const updateGalleryImage = async (
  imageId: number,
  data: {
    caption?: string | null;
    visibility?: number;
    is_spoiler?: boolean;
    game_id?: number | null;
  }
): Promise<GalleryImage> => {
  const response = await api.patch(`/user-gallery/${imageId}/`, data);
  return response.data;
};

// 削除
export const deleteGalleryImage = async (imageId: number): Promise<void> => {
  await api.delete(`/user-gallery/${imageId}/`);
};

// リアクション追加/更新
export const reactToGalleryImage = async (imageId: number, reactionType: number) => {
  const response = await api.post(`/gallery/${imageId}/reaction/`, { reaction_type: reactionType });
  return response.data;
};

// リアクション削除
export const removeGalleryReaction = async (imageId: number) => {
  const response = await api.delete(`/gallery/${imageId}/reaction/`);
  return response.data;
};
